import type { CountryPlaceCategory } from '@/types/country';

export const locationCollectionName = 'locations';
export const GooglePlaceTypesMap = {
    EAT: [
        "acai_shop", "afghani_restaurant", "african_restaurant", "american_restaurant", "asian_restaurant",
        "bagel_shop", "bakery", "bar", "bar_and_grill", "barbecue_restaurant", "brazilian_restaurant",
        "breakfast_restaurant", "brunch_restaurant", "buffet_restaurant", "cafe", "cafeteria", "candy_store",
        "cat_cafe", "chinese_restaurant", "chocolate_factory", "chocolate_shop", "coffee_shop", "confectionery",
        "deli", "dessert_restaurant", "dessert_shop", "diner", "dog_cafe", "donut_shop",
        "fine_dining_restaurant", "food_court", "french_restaurant", "greek_restaurant", "hamburger_restaurant",
        "ice_cream_shop", "indian_restaurant", "indonesian_restaurant", "italian_restaurant", "japanese_restaurant",
        "juice_shop", "korean_restaurant", "lebanese_restaurant", "meal_delivery", "meal_takeaway",
        "mediterranean_restaurant", "mexican_restaurant", "middle_eastern_restaurant", "pizza_restaurant",
        "pub", "ramen_restaurant", "sandwich_shop", "seafood_restaurant", "spanish_restaurant", "steak_house",
        "sushi_restaurant", "tea_house", "thai_restaurant", "turkish_restaurant", "vegan_restaurant",
        "vegetarian_restaurant", "vietnamese_restaurant", "wine_bar", "restaurant"
    ],

    FEATURED_EAT: [
        "restaurant", "cafe", "bar", "bakery", "coffee_shop", "fine_dining_restaurant", "pub", "wine_bar",
        "american_restaurant", "italian_restaurant", "french_restaurant", "chinese_restaurant", "japanese_restaurant",
        "indian_restaurant", "thai_restaurant", "mexican_restaurant", "mediterranean_restaurant", "greek_restaurant",
        "korean_restaurant", "vietnamese_restaurant", "spanish_restaurant", "turkish_restaurant", "middle_eastern_restaurant",
        "pizza_restaurant", "sushi_restaurant", "seafood_restaurant", "steak_house", "barbecue_restaurant",
        "breakfast_restaurant", "brunch_restaurant", "ice_cream_shop", "dessert_shop"
    ],

    STAY: [
        "bed_and_breakfast", "budget_japanese_inn", "campground", "camping_cabin", "cottage",
        "extended_stay_hotel", "farmstay", "guest_house", "hostel", "hotel", "inn", "japanese_inn",
        "lodging", "mobile_home_park", "motel", "private_guest_room", "resort_hotel", "rv_park"
    ],

    FEATURED_STAY: [
        "hotel", "resort_hotel", "hostel", "bed_and_breakfast", "guest_house", "inn", "cottage",
        "extended_stay_hotel", "campground", "lodging"
    ],

    DO: [
        "massage", "sauna", "skin_care_clinic", "spa", "tanning_studio", "wellness_center", "yoga_studio",
        "astrologer", "barber_shop", "beautician", "beauty_salon", "body_art_service", "hair_care",
        "hair_salon", "makeup_artist", "nail_salon", "psychic", "tour_agency", "tourist_information_center",
        "travel_agency", "beach", "arena", "athletic_field", "fishing_charter", "fishing_pond", "fitness_center",
        "golf_course", "gym", "ice_skating_rink", "playground", "ski_resort", "sports_activity_location",
        "sports_club", "sports_coaching", "sports_complex", "stadium", "swimming_pool", "airport", "airstrip",
        "bus_station", "bus_stop", "ferry_terminal", "heliport", "international_airport", "light_rail_station",
        "park_and_ride", "subway_station", "taxi_stand", "train_station", "transit_depot", "transit_station",
        "adventure_sports_center", "amphitheatre", "amusement_center", "amusement_park", "aquarium", "banquet_hall",
        "barbecue_area", "botanical_garden", "bowling_alley", "casino", "childrens_camp", "comedy_club",
        "community_center", "concert_hall", "convention_center", "cultural_center", "cycling_park", "dance_hall",
        "dog_park", "event_venue", "ferris_wheel", "garden", "hiking_area", "historical_landmark", "internet_cafe",
        "karaoke", "marina", "movie_rental", "movie_theater", "national_park", "night_club", "observation_deck",
        "off_roading_area", "opera_house", "park", "philharmonic_hall", "picnic_ground", "planetarium", "plaza",
        "roller_coaster", "skateboard_park", "state_park", "tourist_attraction", "video_arcade", "visitor_center",
        "water_park", "wedding_venue", "wildlife_park", "wildlife_refuge", "zoo", "art_gallery", "art_studio",
        "auditorium", "cultural_landmark", "historical_place", "monument", "museum", "performing_arts_theater",
        "sculpture", "library"
    ],

    FEATURED_DO: [
        "tourist_attraction", "museum", "art_gallery", "park", "national_park", "beach", "zoo", "aquarium",
        "amusement_park", "water_park", "botanical_garden", "historical_landmark", "monument", "cultural_landmark",
        "observation_deck", "tour_agency", "spa", "wellness_center", "massage", "casino", "night_club",
        "movie_theater", "concert_hall", "opera_house", "performing_arts_theater", "comedy_club", "stadium",
        "arena", "golf_course", "ski_resort", "fitness_center", "gym", "hiking_area", "marina", "adventure_sports_center",
        "bowling_alley", "karaoke", "library", "planetarium", "visitor_center", "cultural_center", "garden"
    ],
}

interface GeoPosition {
  lat: number;
  long: number;
}

interface LocationPhoto {
  author?: {
    name?: string;
    photoUri?: string;
  };
  name?: string;
  photo_reference?: string;
}

interface GooglePlacesData {
  addressComponents?: Array<{ types?: string[]; longText?: string; shortText?: string }>;
  address_components?: Array<{ types?: string[]; long_name?: string; short_name?: string }>;
  rating?: number | { $numberDouble?: string };
  userRatingCount?: number | { $numberInt?: string };
  internationalPhoneNumber?: string;
  nationalPhoneNumber?: string;
  formattedAddress?: string;
  shortFormattedAddress?: string;
  adrFormatAddress?: string;
  businessStatus?: string;
  location?: {
    latitude?: number | { $numberDouble?: string };
    longitude?: number | { $numberDouble?: string };
  };
  photos?: Array<{
    authorAttributions?: Array<{ name?: string; photoUri?: string }>;
    name?: string;
    photo_reference?: string;
    url?: string;
  }>;
  primaryType?: string;
  types?: string[];
  displayName?: { text?: string };
  websiteUri?: string;
  goodForChildren?: boolean;
  menuForChildren?: boolean;
  goodForGroups?: boolean;
  reservable?: boolean;
  outdoorSeating?: boolean;
  curbsidePickup?: boolean;
  liveMusic?: boolean;
  servesWine?: boolean;
  servesDessert?: boolean;
  servesBrunch?: boolean;
  servesDinner?: boolean;
  servesCocktails?: boolean;
  servesBeer?: boolean;
  allowsDogs?: boolean;
  servesVegetarianFood?: boolean;
  place_id?: string;
  id?: string;
  formatted_address?: string;
  geometry?: Record<string, unknown>;
  [key: string]: unknown;
}


export class Location {
  _id?: string;

  // Google Places data
  google_place_id?: string;
  /** Hotel-specific Google Place ID for Nuitee hotels (city-level ID is in google_place_id). */
  hotel_google_place_id?: string;
  google_places_data?: GooglePlacesData;
  google_data_age?: number;
  google_photos_age?: number;
  google_rating?: number;
  // Nuitee / third-party hotel data
  /** Nuitee hotel ID for hotels sourced from the Nuitee inventory. */
  nuitee_hotel_id?: string;
  main_photo?: string;
  cost?: number | string;
  budget?: number | string;

  name?: string;
  website?: string;
  city?: string | Record<string, unknown>;
  country?: string;
  /** Tags from the profile countries API: canonical country, US state, and place category. */
  country_name?: string | null;
  state_name?: string | null;
  categories?: CountryPlaceCategory[];
  city_identifying_name?: string;
  /** Plain city name from GET /api/v2/location/:id (null when unknown). */
  city_name?: string | null;
  /** Backend-written summary from the description + recent tips. */
  ai_summary?: string;
  /** Admin-set restaurant links. */
  menu_url?: string;
  opentable_rid?: string;
  reservation_url?: string;
  category_name?: string;
  phone?: string;
  photos?: LocationPhoto[];
  a3_intl_address?: string;
  address?: string;
  organization_status?: string;
  price_level?: string | number;
  geoposition?: {
    type: "Point";
    coordinates: [number, number];
  };
  location_type?: string;
  displayName?: {
    text: string;
  };

  sources?: string[];

  // GT derived fields
  gt_category?: 'eat' | 'stay' | 'do';
  gt_status?: 'active' | 'closed' | 'temporarily_closed';
  gt_family_friendly?: boolean;
  gt_group_friendly?: boolean;
  gt_solo_friendly?: boolean;
  gt_romantic_vibe?: boolean;
  gt_has_live_entertainment?: boolean
  gt_great_for_foodies?: boolean;
  gt_drinks_available?: boolean;
  gt_dog_friendly?: boolean;
  gt_vegetarian_options?: boolean;
  gt_special_occasion?: boolean;

  description?: string;

  // Realtime fields
  rec_references?: {
    user_id: string;
    user_name: string;
    user_small_avatar_url: string;
    user_large_avatar_url: string;
  }[];
  rec_count?: number;
  post_count?: number;

  // Deprecated fields
  geo_position?: GeoPosition;
  trip_advisor_location_id?: string;
  trip_advisor_data?: Record<string, unknown>;
  trip_advisor_rating?: number;
  trip_advisor_photos?: unknown;

  recommendations?: unknown[];
  google_types?: string[];
  constructor(data: Partial<Location> = {}) {
    // Preserve website from data before Object.assign (in case it gets overwritten)
    const websiteFromData = data.website;
    Object.assign(this, data);
    // If website was in data but is now empty/missing, restore it
    if (websiteFromData && !this.website) {
      this.website = websiteFromData;
    }
    if (data.google_places_data) {
      this.extractFromGooglePlacesData();
    }
  }


  private extractFromGooglePlacesData() {
    // Extract city
    if (this.google_places_data?.addressComponents) {
      const cityComponent = this.google_places_data.addressComponents.find(
        (component) => component?.types?.includes?.('locality')
      );
      if (cityComponent) {
        this.city = cityComponent?.longText || cityComponent?.shortText;
      }
    }

    // Extract country
    if (this.google_places_data?.addressComponents) {
      const countryComponent = this.google_places_data.addressComponents.find(
        (component) => component?.types?.includes?.('country')
      );
      if (countryComponent) {
        this.country = countryComponent?.longText || countryComponent?.shortText;
      }
    }

    // Extract city identifying name
    if (this.google_places_data?.addressComponents) {
      const stateComponent = this.google_places_data.addressComponents.find(
        (component) => component?.types?.includes?.('administrative_area_level_1')
      );
      const countryComponent = this.google_places_data.addressComponents.find(
        (component) => component?.types?.includes?.('country')
      );
      if (countryComponent) {
        if (!stateComponent) {
          this.city_identifying_name = countryComponent?.longText || countryComponent?.shortText;
        } else {
          this.city_identifying_name = stateComponent?.longText || stateComponent?.shortText + ', ' + countryComponent?.longText || countryComponent?.shortText;
        }
      }
    }

    // Extract rating
    const rating = this.google_places_data?.rating;
    if (rating && typeof rating === 'object' && '$numberDouble' in rating) {
      this.google_rating = Number(rating.$numberDouble);
    } else if (rating !== undefined) {
      this.google_rating = Number(rating);
    }

    // Extract phone number
    if (this.google_places_data?.internationalPhoneNumber) {
      this.phone = this.google_places_data.internationalPhoneNumber;
    } else if (this.google_places_data?.nationalPhoneNumber) {
      this.phone = this.google_places_data.nationalPhoneNumber;
    }

    // Extract address
    if (this.google_places_data?.formattedAddress) {
      this.a3_intl_address = this.google_places_data.formattedAddress;
    } else if (this.google_places_data?.shortFormattedAddress) {
      this.a3_intl_address = this.google_places_data.shortFormattedAddress;
    } else if (this.google_places_data?.adrFormatAddress) {
      this.a3_intl_address = (this.google_places_data.adrFormatAddress as string).replace(/<[^>]*>/g, '');
    }

    // Extract business status
    if (this.google_places_data?.businessStatus) {
      this.organization_status = this.google_places_data.businessStatus;
    }

    // Handle geo position
    if (this.google_places_data?.location) {
      const googleLat = this.google_places_data.location.latitude;
      const googleLong = this.google_places_data.location.longitude;
      const latVal = googleLat && typeof googleLat === 'object' && '$numberDouble' in googleLat
        ? Number(googleLat.$numberDouble)
        : Number(googleLat);
      const lngVal = googleLong && typeof googleLong === 'object' && '$numberDouble' in googleLong
        ? Number(googleLong.$numberDouble)
        : Number(googleLong);

      this.geo_position = { lat: latVal, long: lngVal };

      // Create geoposition for MongoDB
      this.geoposition = {
        type: "Point",
        coordinates: [this.geo_position.long, this.geo_position.lat],
      };
    }

    // Process Google photos
    if (this.google_places_data?.photos?.length && this.google_places_data.photos.length > 0) {
      this.photos = this.google_places_data.photos.map((img) => ({
        author: img?.authorAttributions?.[0],
        // Store photo identifiers only (name, photo_reference) - URLs generated via photos endpoint
        name: img?.name,
        photo_reference: img?.photo_reference,
      }));
    }

    // Set basic fields
    this.category_name = (this.category_name || this.google_places_data?.primaryType || this.google_places_data?.types?.[0] || this.location_type || '').toLowerCase();
    this.name = (this.google_places_data?.displayName?.text || this.name || (typeof this.city === 'string' ? this.city : '') || '');
    
    // Preserve existing website, or extract from google_places_data, or use empty string
    // Important: Don't overwrite if website already exists (from Object.assign in constructor)
    if (!this.website || this.website === '') {
      if (this.google_places_data?.websiteUri) {
        this.website = this.google_places_data.websiteUri;
      } else if (!this.website) {
        this.website = '';
      }
    }

    this.gt_category = 'do'; // default

    // Determine high-level category based on types
    if (this.google_places_data?.types?.some((type) =>
      GooglePlaceTypesMap.FEATURED_EAT.includes(type)
    )) {
      this.gt_category = 'eat';
    } else if (this.google_places_data?.types?.some((type) =>
      GooglePlaceTypesMap.FEATURED_STAY.includes(type)
    )) {
      this.gt_category = 'stay';
    }

    // Derived filters
    if (this.google_places_data?.goodForChildren || this.google_places_data?.menuForChildren) {
      this.gt_family_friendly = true;
    }

    if (this.google_places_data?.goodForGroups || this.google_places_data?.reservable) {
      this.gt_group_friendly = true;
    }

    // Only apply solo-friendly to 'eat' and 'do' categories
    if (
      ['eat', 'do'].includes(this.gt_category) &&
      (this.google_places_data?.outdoorSeating || this.google_places_data?.curbsidePickup)
    ) {
      this.gt_solo_friendly = true;
    }

    // Romantic vibe is more relevant for 'eat' or high-end 'stay'
    if (
      ['eat', 'stay'].includes(this.gt_category) &&
      (this.google_places_data?.liveMusic || this.google_places_data?.servesWine || this.google_places_data?.servesDessert)
    ) {
      this.gt_romantic_vibe = true;
    }

    if (this.google_places_data?.liveMusic) {
      this.gt_has_live_entertainment = true;
    }

    if (
      this.gt_category === 'eat' &&
      (
        this.google_places_data?.servesBrunch ||
        this.google_places_data?.servesDinner ||
        this.google_places_data?.servesCocktails ||
        this.google_places_data?.servesDessert
      )
    ) {
      this.gt_great_for_foodies = true;
    }

    if (
      this.google_places_data?.servesCocktails ||
      this.google_places_data?.servesBeer ||
      this.google_places_data?.servesWine
    ) {
      this.gt_drinks_available = true;
    }

    if (this.google_places_data?.allowsDogs) {
      this.gt_dog_friendly = true;
    }

    if (this.google_places_data?.servesVegetarianFood) {
      this.gt_vegetarian_options = true;
    }

    if (
      (this.google_places_data?.liveMusic || this.google_places_data?.servesWine || this.google_places_data?.reservable) &&
      ['eat', 'stay'].includes(this.gt_category)
    ) {
      this.gt_special_occasion = true;
    }
  }

  static sanitizeData(location: Partial<Location> & Record<string, unknown> = {}): Partial<Location> {
    return {
      ...(location || {}),
      google_types: location?.google_places_data?.types || [],
      google_places_data: undefined,
      google_data_age: undefined,
      google_photos_age: undefined,
      trip_advisor_photos: undefined,
      trip_advisor_data: undefined,
      trip_advisor_location_id: undefined,
    };
  }
} 

export interface PostLocation {
  _id: string;
  body: string;
  created_at: string;
  image_urls: string[];
  itinerary_day_index: number;
  itinerary_day_order_index: number;
  location_id: string;
  post_id: string;
  published_at: string;
  updated_at: string;
  location?: Location;
  gt_category?: 'eat' | 'stay' | 'do';

  // For itinerary prediction engine
  user_id?: string;
  geolocation?: {
    lat: number;
    long: number;
  };
}

export const postLocationCollectionName = 'post_locations';

/**
 * Returns true if the location document has usable coordinates for map pin rendering.
 * Checks geoposition.coordinates (GeoJSON) first, then google_places_data.location as fallback.
 */
export function locationHasCoordinates(loc: Partial<Location>): boolean {
  return getLocationLngLat(loc) !== null;
}

/** Lat/lng for map math (centroid, bounds). Same sources as locationHasCoordinates. */
export function getLocationLngLat(
  loc: Partial<Location>
): { lat: number; lng: number } | null {
  if (
    loc.geoposition?.coordinates &&
    Array.isArray(loc.geoposition.coordinates) &&
    loc.geoposition.coordinates.length >= 2
  ) {
    const [lng, lat] = loc.geoposition.coordinates;
    if (typeof lat === 'number' && typeof lng === 'number') {
      return { lat, lng };
    }
  }
  const gpdLoc = loc.google_places_data?.location;
  if (gpdLoc) {
    const latRaw = gpdLoc.latitude;
    const lngRaw = gpdLoc.longitude;
    const lat = typeof latRaw === 'object' && latRaw != null ? latRaw.$numberDouble : latRaw;
    const lng = typeof lngRaw === 'object' && lngRaw != null ? lngRaw.$numberDouble : lngRaw;
    if (lat != null && lng != null) {
      return { lat: Number(lat), lng: Number(lng) };
    }
  }
  return null;
}

